import {Component, Input, OnInit, ViewEncapsulation} from '@angular/core';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {AbstractControl, FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {FuseConfirmDialogComponent} from '../../../@fuse/components/confirm-dialog/confirm-dialog.component';
import {InstrumentBrand, InstrumentModel, InstrumentType, Magnitude, MeasurePoint} from '../../interfaces/interfaces';
import {SystemService} from '../../services/system.service';
import {InstrumentService} from '../instrument/instrument.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {DialogConfig} from '../../confirmation/dialog.types';
import {DialogService} from '../../confirmation/dialog.service';
import {map, startWith, switchMap} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {ModelSelectionDialogComponent} from './model-selection-dialog/model-selection-dialog.component';
import {ConfirmationDialogComponent} from '../../confirmation/dialog/dialog.component';
import {UnitsService} from '../../services/units.service';

@Component({
    selector: 'instrument-model',
    templateUrl: './instrument-model.component.html',
    styleUrls: ['./instrument-model.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class InstrumentModelComponent implements OnInit {


    confirmDialogRef: MatDialogRef<ConfirmationDialogComponent>;
    public instrumentModelForm: FormGroup;
    public action: string;
    public instrumentModel: InstrumentModel;
    // public _formArray: BehaviorSubject<AbstractControl[]> = new BehaviorSubject<AbstractControl[]>([]);
    public _magnitudes: BehaviorSubject<Magnitude[]> = new BehaviorSubject<Magnitude[]>([]);

    @Input()
    public title: string;

    // Nuevos controles para autocomplete
    public brandControl: FormControl = new FormControl();
    public familyControl: FormControl = new FormControl();
    public idControl: FormControl = new FormControl();
    public nameControl: FormControl = new FormControl();

    // Nuevas propiedades para las opciones filtradas
    filteredBrands: Observable<InstrumentBrand[]>;
    filteredFamilies: Observable<InstrumentType[]>;

    // Arrays para almacenar opciones
    brands: InstrumentBrand[] = [];
    families: InstrumentType[] = [];

    editingRows: Map<number, boolean> = new Map();

    constructor(public _formBuilder: FormBuilder,
                public _systemService: SystemService,
                public _instrumentService: InstrumentService,
                public unitsService: UnitsService,
                private _matSnackBar: MatSnackBar,
                private _dialogService: DialogService,
                private _matDialog: MatDialog,
                private translate: TranslateService) { }

    ngOnInit(): void {
        this.translate.use(this._systemService.visibility.language);

        // Mantén el código existente
        this._instrumentService.getAllInstrumentType().subscribe(
            allInstruments => this.families = allInstruments
        );

        // Carga las marcas
        this._instrumentService.getAllInstrumentBrands().subscribe(
            brands => this.brands = brands
        );

        // Mantén el filtrado existente
        this.filteredFamilies = this.familyControl.valueChanges.pipe(
            startWith(''),
            map((value) => {
                const name = typeof value === 'string' ? value : value?.name;
                return name ? this._filter(name as string) : this.families.slice();
            }),
        );

        // Configura el filtro para marcas
        this.filteredBrands = this.brandControl.valueChanges.pipe(
            startWith(''),
            map(value => {
                const name = typeof value === 'string' ? value : value?.name;
                return name ? this._filterBrands(name) : this.brands.slice();
            })
        );

    }

    private _filter(name: string): InstrumentType[] {
        const filterValue = name.toLowerCase();
        return this.families.filter(option => option.name?.toLowerCase().includes(filterValue));
    }

    // Métodos para los filtros
    private _filterBrands(value: string): InstrumentBrand[] {
        const filterValue = value.toLowerCase();
        return this.brands.filter(brand => brand.name.toLowerCase().includes(filterValue));
    }

    // Funciones de visualización
    displayBrandFn(brand: InstrumentBrand): string {
        return brand && brand.name ? brand.name : '';
    }

    displayFn(instrumentFamily: InstrumentType): string {
        return instrumentFamily?.name;
    }

    get magnitudes$(): Observable<Magnitude[]>{
        return this._magnitudes.asObservable();
    }

    addModel(): void {

    }

    getFormGroup(lessonForm: AbstractControl): FormGroup {
        return lessonForm as FormGroup;
    }

    getFormControl(value: AbstractControl): FormControl {
        return value as FormControl;
    }

    deleteModel(i: number): void {

    }

    selected(entity): void {
        this.instrumentModel = entity;
        if (entity) {
            this._instrumentService.getModel(entity.id).subscribe(
                (data) => {
                    this.instrumentModel = data;
                    this._magnitudes.next(data.magnitudes);
                    this.instrumentModelForm = this.createInstrumentModelForm();
                }
            );
            // this._instrumentService.getInstrumentModels(entity.id).subscribe(
            //     (data) => {
            //         this._instrumentModels.next(data);
            //     }
            // );



            // this.attributes = [];
            // this.getAttributes(entity);
            // this.ngOnInit();
        }
    }



    createInstrumentModelForm(): FormGroup {
        // Establecer los valores iniciales para los controles existentes
        this.idControl.setValue(this.instrumentModel.id);
        this.nameControl.setValue(this.instrumentModel.name);
        this.brandControl.setValue(this.instrumentModel.brand);
        this.familyControl.setValue(this.instrumentModel.family);

        return this._formBuilder.group({
            id: this.idControl,
            name: this.nameControl,
            brand: this.brandControl,  // Usar el control existente en lugar de crear uno nuevo
            family: this.familyControl // Usar el control existente en lugar de crear uno nuevo

        });
    }

    deleteInstrumentModel(): void {

    }

    guardarInstrumentModel(): void {
        if (this.instrumentModelForm.valid) {
            const updatedModel: InstrumentModel = new InstrumentModel({
                id: this.idControl.value,
                name: this.nameControl.value,
                brand: this.brandControl.value,
                family: this.familyControl.value,
            });

            const brandValue = this.brandControl.value as InstrumentBrand | string;

            // Si la marca no existe (string) o no tiene id válido, crearla antes de guardar el modelo
            const ensureBrand$ = ((): Observable<InstrumentBrand> => {
                if (!brandValue) {
                    return of(null);
                }
                if (typeof brandValue === 'string') {
                    return this._instrumentService.createOrUpdateBrand({ id: 0, name: brandValue });
                }
                if (!brandValue.id || brandValue.id <= 0) {
                    return this._instrumentService.createOrUpdateBrand({ id: 0, name: brandValue.name });
                }
                return of(brandValue);
            })();

            ensureBrand$.pipe(
                switchMap((brand: InstrumentBrand) => {
                    if (brand) {
                        updatedModel.brand = brand;
                    }
                    return this._instrumentService.updateInstrumentModel(updatedModel);
                })
            ).subscribe(
                (data) => {
                    this._matSnackBar.open('Modelo de instrumento actualizado', 'OK', {
                        verticalPosition: 'top',
                        duration: 4000
                    });
                    this.selected(data);
                },
                () => {
                    this._matSnackBar.open('Error al actualizar el modelo de instrumento', 'OK', {
                        verticalPosition: 'top',
                        duration: 8000,
                        panelClass: 'error-snackbar'
                    });
                }
            );
        } else {
            this._matSnackBar.open('Formulario inválido, por favor corrige los errores', 'OK', {
                verticalPosition: 'top',
                duration: 8000,
                panelClass: 'error-snackbar'
            });
        }
    }

    // Verifica si una fila está en edición
    isEditing(fila: any): boolean {
        return this.editingRows.get(fila.id) || fila.id <= 0;
    }

    compareUnits(unit1: string, unit2: string): boolean {
        return unit1 && unit2 ? unit1.trim() === unit2.trim() : unit1 === unit2;
    }


    private compareMeasurePoints(a: MeasurePoint, b: MeasurePoint): number {
        return a.range * this.unitsService.getConversionRate(a.unit) - b.range * this.unitsService.getConversionRate(b.unit);
    }

    // Devuelve los puntos de medida ordenados para mostrar en la vista, sin mutar el arreglo original
    getSortedMeasurePoints(magnitud: Magnitude): MeasurePoint[] {
        return (magnitud?.measurePoints || []).slice().sort((a, b) => this.compareMeasurePoints(a, b));
    }

    editarFila(fila: any): void {
        this.editingRows.set(fila.id, true);
    }

    // Guarda los cambios y desactiva la edición
    aceptarEdicion(fila: MeasurePoint, magnitud: Magnitude): void {
        // Aquí puedes agregar la lógica para guardar los cambios en la fila
        // Por ejemplo, enviar una solicitud HTTP para actualizar los datos en el servidor
        const copiaFila = {...fila}; // Crea una copia de la fila original
        if (fila.id <= 0) {
            copiaFila.id = null;
        }

        this._instrumentService.updateMeasurePoint(magnitud.id, copiaFila).subscribe(
            (nuevoFila) => {
                this.editingRows.delete(fila.id);
                // actualizar magnitude con el nuevo MeasurePoint
                const index = magnitud.measurePoints.findIndex(p => p.id === fila.id);
                if (index !== -1) {
                    magnitud.measurePoints[index] = nuevoFila;
                } else {
                    magnitud.measurePoints.push(nuevoFila);
                }
                // Ordenar in-place por unidad (según UnitsService) y luego por range
                magnitud.measurePoints.sort((a, b) => this.compareMeasurePoints(a, b));

            },
            (error) => {
                this._matSnackBar.open('Error: Hubo un error al actualizar el punto' , 'OK', {
                    verticalPosition: 'top',
                    duration        : 6000,
                    panelClass: 'error-snackbar'
                });
            }
        );

    }

    // Cancela la edición y restaura los valores originales
    cancelarEdicion(fila: MeasurePoint, magnitud: Magnitude): void {
        if (fila.id < 0){
            magnitud.measurePoints = magnitud.measurePoints.filter(f => f.id !== fila.id);
        }else{

        }
        this.editingRows.delete(fila.id);
    }

    // Elimina una fila de la magnitud
    borrarFila(fila: any, magnitud: Magnitude): void {
        this._instrumentService.deleteMeasurePoint(fila.id).subscribe(
            () => {
                magnitud.measurePoints = magnitud.measurePoints.filter(f => f.id !== fila.id);
                this._matSnackBar.open('Punto de medida eliminado', 'OK', {
                    verticalPosition: 'top',
                    duration        : 4000
                });
            },
            (error) => {
                this._matSnackBar.open('Error: Hubo un error al eliminar el punto' , 'OK', {
                    verticalPosition: 'top',
                    duration        : 6000,
                    panelClass: 'error-snackbar'
                });
            }
        );
    }

    agregarPuntoMedida(magnitud: Magnitude): void {
        const nuevoPuntoMedida: MeasurePoint = {
            id: 0
        };
        magnitud.measurePoints.push(nuevoPuntoMedida);
    }

    duplicarFila(fila: any, magnitud: Magnitude): void {
        const nuevaFila = {...fila}; // Crea una copia de la fila original
        // asinga un numero aleatorio menor que 0
        const aleatorio = Math.floor(Math.random() * 1000);
        nuevaFila.id =  aleatorio < 0 ? aleatorio : -aleatorio ; // Asigna un nuevo ID
        this.editingRows.set(nuevaFila.id, true); // Marca la nueva fila como en edición
        magnitud.measurePoints.push(nuevaFila); // Agrega la nueva fila a la magnitud
    }

    formatearConPrecision(numero: number, precision: number): string {
        if (numero === null || numero === undefined || isNaN(numero)) {
            return '';
        }

        if (!precision || precision <= 0) {
            return numero.toString().replace(/\./g, ',');
        }

        // Calcular el número de decimales basado en la precisión
        const decimales = Math.log10(1 / precision);

        // Validar que decimales esté en el rango permitido por toFixed (0-100)
        if (!Number.isFinite(decimales) || decimales < 0 || decimales > 100) {
            console.warn(`Precisión inválida: ${precision}, decimales calculados: ${decimales}`);
            return numero.toString().replace(/\./g, ',');
        }

        // Redondear el número a la precisión especificada
        const rounded = Math.round(numero / precision) * precision;

        // Formatear con la cantidad correcta de decimales, asegurando que sea un entero
        return rounded.toFixed(Math.floor(decimales)).replace(/\./g, ',');
    }

    // formatearConPrecision(numero: number, precision: number): string {
    //     if (numero === null || numero === undefined) {
    //         return '';
    //     }
    //     if (!precision || precision <= 0) {
    //         return numero.toString();
    //     }
    //     const decimales = Math.log10(1 / precision);
    //     return numero.toFixed(decimales);
    // }


    addMagnitude(model: InstrumentModel, brand: InstrumentBrand ): void {
        const instrumentType: InstrumentType  = model.family;
        const values: string[] = instrumentType.icc.posibleMagnitudes ? instrumentType.icc.posibleMagnitudes.split(/[,;]/) : [];
        const dialogConfig: DialogConfig = {
            values: values,
            icon: {
                show: false
            },
            dismissible: true,
            title: 'Seleccionar magnitud',
            message: null,
            label: 'Magnitud',
            actions: {
                confirm: {
                    label: 'Agregar',
                    color: 'primary',
                },
                cancel: {
                    label: 'Cancelar'
                }
            }
        };
        const dialogRef = this._dialogService.openSelect(dialogConfig);

        dialogRef.afterClosed().subscribe(result => {
            if (result.action === 'confirmed') {
                const newMagnitude: Magnitude = {
                    id: null,
                    name: result.selection,
                    label: result.selection
                };
                this._instrumentService.addOrUpdateMagnitude(model.id, newMagnitude).subscribe(
                    (addedMagnitude) => {
                        addedMagnitude.measurePoints = [];
                        this._magnitudes.next([...this._magnitudes.getValue(), addedMagnitude]);
                        this.instrumentModel.magnitudes = [...this.instrumentModel.magnitudes, addedMagnitude];
                        this._matSnackBar.open('Magnitud agregada', 'OK', {
                            verticalPosition: 'top',
                            duration: 4000
                        });
                    },
                    (error) => {
                        this._matSnackBar.open('Error: Hubo un error al agregar la magnitud', 'OK', {
                            verticalPosition: 'top',
                            duration: 6000,
                            panelClass: 'error-snackbar'
                        });
                    }
                );
            }
        });
    }

    deleteMagnitude(magnitude: Magnitude): void {
        this.confirmDialogRef = this._dialogService.open( {
            title: 'Borrar Magnitud',
            message: `¿Desea eliminar la magnitud ${magnitude.name}?`,
            icon: {color: 'warn'},
            actions: {
                confirm: {show: true, color: 'primary'},
                no: {show: true, color : 'warn'},
                cancel: {show: false}
            }
        });

        this.confirmDialogRef.afterClosed().subscribe(result => {
            if (result === 'confirmed') {
                this._instrumentService.deleteMagnitude(magnitude.id).subscribe(
                    () => {
                        const currentMagnitudes = this._magnitudes.getValue();
                        const updatedMagnitudes = currentMagnitudes.filter(m => m.id !== magnitude.id);
                        this._magnitudes.next(updatedMagnitudes);
                        this.instrumentModel.magnitudes = updatedMagnitudes;
                        this._matSnackBar.open('Magnitud eliminada', 'OK', {
                            verticalPosition: 'top',
                            duration: 4000
                        });
                    },
                    (error) => {
                        this._matSnackBar.open('Error: Hubo un error al eliminar la magnitud', 'OK', {
                            verticalPosition: 'top',
                            duration: 6000,
                            panelClass: 'error-snackbar'
                        });
                    }
                );
            }
        });
    }

    agregarMagnitudesDeOtroModelo(): void {
        // Obtener todos los modelos disponibles

        const family: InstrumentType = this.familyControl.value;

        if (!(family && family.icc)) {
            // mensaje diciendo que debe seleccionar una familia
            this._matSnackBar.open('Debe seleccionar una familia para agregar magnitudes', 'Cerrar', { duration: 4000 });
            return;
        }


        this._instrumentService.getAllModelsByICCWithMagnitudes(family.icc.id).subscribe((models) => {
            const dialogRef = this._matDialog.open(ModelSelectionDialogComponent, {
                width: '500px',
                data: {models}
            });

            dialogRef.afterClosed().subscribe((selectedModel: InstrumentModel) => {
                if (selectedModel && selectedModel.id) {

                    const currentModel = this.instrumentModel;
                    // Obtener las magnitudes del modelo seleccionado
                    this._instrumentService.getMagnitudes(selectedModel.id).subscribe(
                        (magnitudes) => {
                            if (!magnitudes || magnitudes.length === 0) {
                                this._matSnackBar.open('El modelo seleccionado no tiene magnitudes disponibles', 'Cerrar', {duration: 4000});
                                return;
                            }

                            this._instrumentService.importMagnitudes(selectedModel.id, currentModel.id).subscribe(
                                (newMagnitudes) => {
                                    // Actualizar la lista de magnitudes en el servicio
                                    this._magnitudes.next(newMagnitudes);
                                    this.instrumentModel.magnitudes = newMagnitudes;

                                    this._matSnackBar.open(
                                        `Se importaron ${newMagnitudes.length} magnitudes del modelo ${selectedModel.name}`,
                                        'Cerrar',
                                        {duration: 4000}
                                    );
                                },
                                (error) => {
                                    this._matSnackBar.open('Error al importar las magnitudes', 'Cerrar', {duration: 4000});
                                    console.error('Error al importar magnitudes:', error);
                                }
                            );
                        },
                        (error) => {
                            this._matSnackBar.open('Error al obtener magnitudes del modelo seleccionado', 'Cerrar', {duration: 4000});
                            console.error('Error al obtener magnitudes:', error);
                        }
                    );
                }
            });
        });
    }

    onMagnitudLabelBlur(magnitud: Magnitude): void {
        if (!magnitud) {
            return;
        }
        // const toSave: Magnitude = {
        //     id: magnitud.id ?? null,
        //     name: magnitud.name,
        //     label: (magnitud.label || '').trim()
        //     maxRange:
        // };

        const toSave = magnitud;
        toSave.maxRange = magnitud.maxRange || null;
        toSave.minRange = magnitud.minRange || null;

        this._instrumentService.addOrUpdateMagnitude(this.instrumentModel.id, toSave).subscribe(
            (saved) => {
                // Sincronizar datos relevantes
                magnitud.id = saved?.id ?? magnitud.id;
                magnitud.label = saved?.label ?? magnitud.label;
                magnitud.name = saved?.name ?? magnitud.name;
                magnitud.maxRange = saved.maxRange || null;
                magnitud.minRange = saved.minRange || null;
            },
            () => {
                this._matSnackBar.open('Error: Hubo un error al actualizar la magnitud', 'OK', {
                    verticalPosition: 'top',
                    duration: 6000,
                    panelClass: 'error-snackbar'
                });
            }
        );
    }

    // Obtener las magnitudes ordenadas por weightOrder
    getSortedMagnitudes(): Magnitude[] {
        if (!this.instrumentModel?.magnitudes) {
            return [];
        }
        return [...this.instrumentModel.magnitudes].sort((a, b) => (a.weightOrder || 0) - (b.weightOrder || 0));
    }

    // Verificar si es la primera magnitud
    isFirstMagnitude(magnitud: Magnitude): boolean {
        const sorted = this.getSortedMagnitudes();
        return sorted.length > 0 && sorted[0].id === magnitud.id;
    }

    // Verificar si es la última magnitud
    isLastMagnitude(magnitud: Magnitude): boolean {
        const sorted = this.getSortedMagnitudes();
        return sorted.length > 0 && sorted[sorted.length - 1].id === magnitud.id;
    }

    // Mover magnitud hacia arriba
    moveMagnitudeUp(magnitud: Magnitude): void {
        const sorted = this.getSortedMagnitudes();
        const currentIndex = sorted.findIndex(m => m.id === magnitud.id);

        if (currentIndex <= 0) {
            return; // Ya está en la primera posición
        }

        const previousMagnitud = sorted[currentIndex - 1];

        // Intercambiar weightOrder
        const tempWeight = magnitud.weightOrder;
        magnitud.weightOrder = previousMagnitud.weightOrder - 1;
        previousMagnitud.weightOrder = tempWeight;

        // Actualizar ambas magnitudes en el servidor
        this._instrumentService.addOrUpdateMagnitude(this.instrumentModel.id, magnitud).pipe(
            switchMap(() => this._instrumentService.addOrUpdateMagnitude(this.instrumentModel.id, previousMagnitud)),
            switchMap(() => this._instrumentService.getMagnitudes(this.instrumentModel.id))
        ).subscribe(
            (magnitudes) => {
                this.instrumentModel.magnitudes = magnitudes;
                this._magnitudes.next(magnitudes);
                this._matSnackBar.open('Magnitud movida hacia arriba', 'OK', {
                    verticalPosition: 'top',
                    duration: 2000
                });
            },
            () => {
                this._matSnackBar.open('Error: Hubo un error al reordenar la magnitud', 'OK', {
                    verticalPosition: 'top',
                    duration: 6000,
                    panelClass: 'error-snackbar'
                });
            }
        );
    }

    // Mover magnitud hacia abajo
    moveMagnitudeDown(magnitud: Magnitude): void {
        const sorted = this.getSortedMagnitudes();
        const currentIndex = sorted.findIndex(m => m.id === magnitud.id);

        if (currentIndex < 0 || currentIndex >= sorted.length - 1) {
            return; // Ya está en la última posición
        }

        const nextMagnitud = sorted[currentIndex + 1];

        // Intercambiar weightOrder
        const tempWeight = magnitud.weightOrder;
        magnitud.weightOrder = nextMagnitud.weightOrder + 1;
        nextMagnitud.weightOrder = tempWeight;

        // Actualizar ambas magnitudes en el servidor
        this._instrumentService.addOrUpdateMagnitude(this.instrumentModel.id, magnitud).pipe(
            switchMap(() => this._instrumentService.addOrUpdateMagnitude(this.instrumentModel.id, nextMagnitud)),
            switchMap(() => this._instrumentService.getMagnitudes(this.instrumentModel.id))
        ).subscribe(
            (magnitudes) => {
                this.instrumentModel.magnitudes = magnitudes;
                this._magnitudes.next(magnitudes);
                this._matSnackBar.open('Magnitud movida hacia abajo', 'OK', {
                    verticalPosition: 'top',
                    duration: 2000
                });
            },
            () => {
                this._matSnackBar.open('Error: Hubo un error al reordenar la magnitud', 'OK', {
                    verticalPosition: 'top',
                    duration: 6000,
                    panelClass: 'error-snackbar'
                });
            }
        );
    }

}
